import axios from 'axios';

/**
 * Azure AD Authentication Service
 *
 * Supports Azure App Service Easy Auth:
 * - Direct SSO user discovery via `/.auth/me`
 * - Optional EasyAuth session -> local JWT exchange via `/api/auth/azure-login`
 */

const API_BASE_URL = '/api';
const AZURE_AUTH_ME_ENDPOINT = '/.auth/me';
const AZURE_AUTH_LOGIN_ENDPOINT = '/.auth/login/aad';
const AZURE_AUTH_LOGOUT_ENDPOINT = '/.auth/logout';

const isProbablyAzureHosted = () => {
  const host = window.location.hostname || '';
  return host.includes('azurewebsites.net') || host.includes('islandmillworkinc.com');
};

const normalizeRoleValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim().toLowerCase();
  const exactMatches = {
    admin: 'admin',
    administrator: 'admin',
    estimator: 'estimator',
  };

  if (exactMatches[normalized]) return exactMatches[normalized];

  const qualifiedSegments = normalized.split(/[.:/_\s]+/).filter(Boolean);
  const trailingSegment = qualifiedSegments[qualifiedSegments.length - 1];
  if (trailingSegment && exactMatches[trailingSegment]) {
    return exactMatches[trailingSegment];
  }

  return null;
};

const resolveRoleFromClaims = (claims = {}) => {
  const rawRoles = [
    claims.roles,
    claims.role,
    claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
  ].filter(Boolean);

  const flattened = rawRoles.flatMap((value) =>
    Array.isArray(value) ? value : [value]
  );

  for (const value of flattened) {
    const normalizedRole = normalizeRoleValue(value);
    if (normalizedRole) return normalizedRole;
  }

  return null;
};

/**
 * Check if we're running on Azure with Easy Auth enabled.
 * In local development, this will return false.
 */
export const isAzureAuthEnabled = () => {
  return isProbablyAzureHosted();
};

/**
 * Attempts to exchange an existing EasyAuth session for local JWT tokens.
 * Returns the backend payload on success, otherwise null.
 */
export const tryAzureJwtExchange = async () => {
  try {
    if (!isProbablyAzureHosted()) return null;

    const response = await axios.get(`${API_BASE_URL}/auth/azure-login`, {
      withCredentials: true,
      headers: { 'Cache-Control': 'no-cache' },
    });

    return response.data;
  } catch (error) {
    if (error.response?.status === 401) return null;
    console.error('Azure JWT exchange failed:', error.response?.data || error.message);
    return null;
  }
};

/**
 * Get the current Azure AD authenticated user.
 * Returns null if not authenticated or not on Azure.
 */
export const getAzureUser = async () => {
  if (!isAzureAuthEnabled()) {
    console.log('Azure Auth: Not running on Azure, skipping Azure AD check');
    return null;
  }

  try {
    const response = await fetch(AZURE_AUTH_ME_ENDPOINT, {
      credentials: 'include',
    });

    if (!response.ok) {
      console.log('Azure Auth: User not authenticated via Azure AD');
      return null;
    }

    const data = await response.json();

    if (!data || data.length === 0) {
      return null;
    }

    const aadUser = data[0];

    const claims = {};
    if (aadUser.user_claims) {
      aadUser.user_claims.forEach((claim) => {
        claims[claim.typ] = claim.val;
      });
    }

    const resolvedRole = resolveRoleFromClaims(claims);
    const isAdmin = resolvedRole === 'admin';

    const user = {
      id:
        claims['http://schemas.microsoft.com/identity/claims/objectidentifier'] ||
        aadUser.user_id,
      email:
        claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
        claims['preferred_username'] ||
        aadUser.user_id,
      name:
        claims['name'] ||
        claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
        aadUser.user_id,
      firstName:
        claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] ||
        '',
      lastName:
        claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] ||
        '',
      provider: 'azure_ad',
      provider_id: aadUser.provider_name,
      access_token: aadUser.access_token,
      id_token: aadUser.id_token,
      claims,
      role: resolvedRole || 'none',
      is_admin: isAdmin,
    };

    console.log('Azure Auth: User authenticated:', user.email);
    return user;
  } catch (error) {
    console.error('Azure Auth: Error fetching user info:', error);
    return null;
  }
};

/**
 * Redirect to Azure AD login.
 */
export const loginWithAzureAD = (returnUrl = window.location.href) => {
  const encodedReturnUrl = encodeURIComponent(returnUrl);
  window.location.href = `${AZURE_AUTH_LOGIN_ENDPOINT}?post_login_redirect_uri=${encodedReturnUrl}`;
};

/**
 * Logout from Azure AD and redirect.
 */
export const logoutFromAzureAD = (returnUrl = '/') => {
  const encodedReturnUrl = encodeURIComponent(window.location.origin + returnUrl);
  window.location.href = `${AZURE_AUTH_LOGOUT_ENDPOINT}?post_logout_redirect_uri=${encodedReturnUrl}`;
};

export const azureLogoutRedirect = () => {
  window.location.href = '/.auth/logout?post_logout_redirect_uri=/login';
};

/**
 * Get the Azure AD access token for API calls.
 */
export const getAzureAccessToken = async () => {
  const user = await getAzureUser();
  return user?.access_token || null;
};

/**
 * Refresh the Azure AD token.
 */
export const refreshAzureToken = async () => {
  if (!isAzureAuthEnabled()) {
    return null;
  }

  try {
    const response = await fetch('/.auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      return await getAzureUser();
    }
    return null;
  } catch (error) {
    console.error('Azure Auth: Token refresh failed:', error);
    return null;
  }
};

const azureAuthService = {
  isAzureAuthEnabled,
  getAzureUser,
  loginWithAzureAD,
  logoutFromAzureAD,
  getAzureAccessToken,
  refreshAzureToken,
  tryAzureJwtExchange,
  azureLogoutRedirect,
  isProbablyAzureHosted,
};

export { isProbablyAzureHosted };

export default azureAuthService;
